import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

type CalendlyInlineWidgetProps = {
  url: string;
  title: string;
  redirectOnScheduleTo?: string;
};

const CalendlyInlineWidget = ({ url, title, redirectOnScheduleTo }: CalendlyInlineWidgetProps) => {
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const loadedRef = useRef(false);
  const [loadKey, setLoadKey] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const embedUrl = useMemo(() => {
    const nextUrl = new URL(url);
    nextUrl.searchParams.set("hide_gdpr_banner", "1");
    nextUrl.searchParams.set("background_color", "ffffff");
    nextUrl.searchParams.set("text_color", "0a0a0a");
    nextUrl.searchParams.set("primary_color", "0a0a0a");
    nextUrl.searchParams.set("embed_type", "Inline");

    if (typeof window !== "undefined") {
      nextUrl.searchParams.set("embed_domain", window.location.hostname);
    }

    return nextUrl.toString();
  }, [url]);

  useEffect(() => {
    loadedRef.current = false;
    setLoaded(false);

    const retryTimer = window.setTimeout(() => {
      if (!loadedRef.current) {
        setLoadKey((current) => current + 1);
      }
    }, 6500);

    return () => window.clearTimeout(retryTimer);
  }, [embedUrl, loadKey]);

  useEffect(() => {
    if (!redirectOnScheduleTo) return;
    const handler = (e: MessageEvent) => {
      const data = e.data;
      if (
        data &&
        typeof data === "object" &&
        typeof (data as any).event === "string" &&
        (data as any).event.indexOf("calendly.") === 0 &&
        (data as any).event === "calendly.event_scheduled"
      ) {
        navigate(redirectOnScheduleTo);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [redirectOnScheduleTo, navigate]);

  const handleLoad = () => {
    loadedRef.current = true;
    setLoaded(true);
  };

  return (
    <div className="relative rounded-xl overflow-hidden border border-border bg-background shadow-sm min-h-[760px] md:min-h-[860px]">
      {!loaded && (
        <div className="absolute inset-0 flex items-start justify-center pt-6 bg-background">
          <div className="flex gap-4" aria-hidden="true">
            <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground animate-pulse" />
            <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground animate-pulse [animation-delay:150ms]" />
            <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground animate-pulse [animation-delay:300ms]" />
          </div>
        </div>
      )}
      <iframe
        key={loadKey}
        ref={iframeRef}
        src={embedUrl}
        title={title}
        aria-label={title}
        loading="eager"
        onLoad={handleLoad}
        className="relative z-10 block w-full min-w-[320px] h-[760px] md:h-[860px] bg-background"
        allow="camera; microphone; fullscreen; payment"
      />
    </div>
  );
};

export default CalendlyInlineWidget;